import { useCallback, useEffect, useMemo, useState } from 'react'
import { ValidationStatus } from 'types/shared'
import { VALIDATION_STATUSES } from 'utils/constants'
import { useGroupValidationContext } from './GroupValidationContext'

type useValidateGroup = {
  groupActivities: string[]
  uuid: string
  dependency?: string
}

const useValidateGroup = ({
  groupActivities,
  uuid,
  dependency
}: useValidateGroup) => {
  const [activitiesValidationStatus, setActivitiesValidationStatus] = useState<
    Record<string, ValidationStatus>
  >({})

  const { setActivityValidationStatus, groupsStatus } =
    useGroupValidationContext()

  const validateActivity = useCallback(
    (activityUuid: string, validationStatus?: ValidationStatus) => {
      if (!groupActivities.includes(activityUuid)) {
        return
      }

      setActivityValidationStatus(
        activityUuid,
        validationStatus || VALIDATION_STATUSES.PENDING
      )
      setActivitiesValidationStatus((prev) => ({
        ...prev,
        [activityUuid]: validationStatus || VALIDATION_STATUSES.VALID
      }))
    },
    [groupActivities, setActivityValidationStatus]
  )

  useEffect(() => {
    const statuses = Object.values(activitiesValidationStatus)
    const groupStatus = statuses.includes(VALIDATION_STATUSES.INVALID)
      ? VALIDATION_STATUSES.INVALID
      : VALIDATION_STATUSES.VALID

    setActivityValidationStatus(uuid, groupStatus)
  }, [activitiesValidationStatus, setActivityValidationStatus, uuid])

  const status = useMemo(() => groupsStatus[uuid], [groupsStatus, uuid])

  const isDisabled = useMemo(() => {
    if (!dependency || !groupsStatus[dependency]) {
      return false
    }

    return groupsStatus[dependency] !== VALIDATION_STATUSES.VALID
  }, [dependency, groupsStatus])

  return {
    validateActivity,
    status,
    isDisabled
  }
}

export default useValidateGroup
